import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { useNotifications, notificationService } from './ui/NotificationSystem';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { X, DollarSign, CreditCard, Banknote, FileCheck, Landmark, Smartphone, type LucideIcon } from 'lucide-react';
import { CustomSelect } from './ui/CustomSelect';
import { StripePaymentModal } from './ui/StripePaymentModal';
import { NumericInput } from './ui/NumericInput';
import { PaymentDTO, PaymentType, PaymentDispatcher, CompanyPaymentMethodConfigDTO } from '@/types';
import type { Payable } from '@/hooks/usePayable';
import { amountDueNow, roundCurrency } from '@/lib/invoiceCalculations';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';
import { useJobEditPermissions } from '@/hooks/useJobEditPermissions';
import {
    checkTapToPaySupport,
    ensureReaderWarm,
    hasCompletedTapToPayOnboarding,
    hasTapToPayLocationPermission,
    recordTapToPayDiagnostic,
    tapToPayName,
    tapToPaySetupPrompt,
    type TapToPaySupportResult,
} from '@/lib/tapToPay';
import { useTapToPaySetup } from '@/hooks/useTapToPaySetup';
import { useTapToPay, type PrefetchedTerminalIntent } from '@/hooks/useTapToPay';
import { TapToPayModal } from './TapToPayModal';
import { TapToPayIcon } from './ui/icons/TapToPayIcon';

/** Settle time after the last amount change before we spend a PaymentIntent on it. */
const INTENT_PREFETCH_DEBOUNCE_MS = 400;

const PAYMENT_METHOD_LABELS: Record<PaymentType, string> = {
    Cash: 'Cash',
    CreditCard: 'Credit Card',
    Check: 'Check',
    ACHWire: 'ACH / Wire',
    ZelleVenmo: 'Zelle / Venmo',
};

const PAYMENT_METHOD_ICONS: Record<PaymentType, { icon: LucideIcon; color: string }> = {
    Cash: { icon: Banknote, color: 'text-green-600' },
    CreditCard: { icon: CreditCard, color: 'text-blue-600' },
    Check: { icon: FileCheck, color: 'text-purple-600' },
    ACHWire: { icon: Landmark, color: 'text-amber-600' },
    ZelleVenmo: { icon: Smartphone, color: 'text-sky-600' },
};

interface TakePaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** The job or estimate being paid (see useJobPayable / useEstimatePayable). */
    payable: Payable;
}

/** Scroll focused field into the visual viewport (above keyboard) inside `scrollRoot`. */
function scrollFocusedFieldIntoVisualViewport(scrollRoot: HTMLElement, padding = 12): void {
    const active = document.activeElement;
    if (!active || !(active instanceof HTMLElement)) return;
    if (!scrollRoot.contains(active)) return;

    const editable =
        active.matches('input, textarea, select') ||
        active.getAttribute('role') === 'combobox' ||
        !!active.closest('[role="combobox"]');
    if (!editable) return;

    const vv = window.visualViewport;
    if (!vv) {
        active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        return;
    }

    const rect = active.getBoundingClientRect();
    const visibleTop = vv.offsetTop + padding;
    const visibleBottom = vv.offsetTop + vv.height - padding;

    let delta = 0;
    if (rect.bottom > visibleBottom) delta = rect.bottom - visibleBottom;
    else if (rect.top < visibleTop) delta = rect.top - visibleTop;

    if (delta === 0) return;

    let node: HTMLElement | null = active;
    while (node && scrollRoot.contains(node)) {
        const parent: HTMLElement | null = node.parentElement;
        if (!parent || !scrollRoot.contains(parent)) break;
        const style = window.getComputedStyle(parent);
        const scrollable =
            (style.overflowY === 'auto' || style.overflowY === 'scroll') &&
            parent.scrollHeight > parent.clientHeight + 1;
        if (scrollable) {
            parent.scrollTop += delta;
            return;
        }
        node = parent;
    }
}

export function TakePaymentModal({ isOpen, onClose, payable }: TakePaymentModalProps) {
    const { addNotification } = useNotifications();
    const modalScrollRootRef = useRef<HTMLDivElement>(null);

    // ── Form state ────────────────────────────────────────────────────
    const [paymentType, setPaymentType] = useState<PaymentType>('Cash');
    const [amount, setAmount] = useState(0);
    const [processedBy, setProcessedBy] = useState<PaymentDispatcher>('Company');
    const [reference, setReference] = useState('');
    /** Card payment (Tap to Pay / manual card) or Record payment (cash, check…). */
    const [mode, setMode] = useState<'card' | 'record'>('card');

    // ── Manual card entry sub-modal ───────────────────────────────────
    const [showStripeModal, setShowStripeModal] = useState(false);
    const [tapToPaySupport, setTapToPaySupport] = useState<TapToPaySupportResult | null>(null);

    const [tapToPayOnboarded, setTapToPayOnboarded] = useState(true);

    // ── Tap to Pay ────────────────────────────────────────────────────
    // Owned here, alongside the amount field, so the prominent checkout button
    // (App Review checklist §5.1/§5.2) has one reader connection, one hook and one
    // prefetched PaymentIntent. Two hooks would mean two warms racing for the same
    // reader — which is what happened while this lived in the old charge-card sheet.
    const tapToPay = useTapToPay();
    const prefetchedIntentRef = useRef<PrefetchedTerminalIntent | null>(null);
    const [showTapToPayModal, setShowTapToPayModal] = useState(false);
    const [tapToPayAmount, setTapToPayAmount] = useState(0);
    /** Explains why Tap to Pay can't run right now. The button itself is never disabled. */
    const [tapToPayBlockedReason, setTapToPayBlockedReason] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            checkTapToPaySupport().then(setTapToPaySupport);
            hasCompletedTapToPayOnboarding().then(setTapToPayOnboarded);
        }
    }, [isOpen]);

    const { allowedPaymentTypes, canUseTapToPay } = useJobEditPermissions();

    const layer = useModalLayer({
        enabled: isOpen && !showStripeModal,
        onClose,
    });

    // ── Derived values ────────────────────────────────────────────────
    const totalPaid = payable.payments.reduce((sum, p) => sum + p.amount, 0);
    const total = payable.total;
    const remainingBalance = roundCurrency(total - totalPaid);
    // Prefill with what's still owed on the deposit; once it's met, the rest of the balance
    const defaultAmount = amountDueNow(total, totalPaid, payable.depositAmount) || remainingBalance;
    // Primitives, so effects keyed on the target don't re-run on every render.
    const targetKind = payable.target.kind;
    const targetId = payable.target.id;

    // ── Queries ───────────────────────────────────────────────────────
    const { data: paymentConfigs = [], isLoading: configsLoading } = useQuery({
        queryKey: ['paymentMethodConfigs', payable.companyId],
        queryFn: () => apiService.getPaymentMethodConfigs(),
        enabled: isOpen && !!payable.companyId,
    });

    const { data: providerStatus } = useQuery({
        queryKey: ['paymentProviderStatus', payable.companyId],
        queryFn: () => apiService.getPaymentProviderStatus(),
        enabled: isOpen && !!payable.companyId,
        staleTime: 5 * 60 * 1000,
    });

    const isStripeConnected = providerStatus?.connected ?? false;
    const isStripeReady = isStripeConnected && (providerStatus?.charges_enabled ?? false);
    const connectedAccountId = providerStatus?.account_id;
    const terminalLocationId = providerStatus?.terminal_location_id;

    // Warm the reader as soon as the user opens a payment screen, so the reader
    // is connected before they ask to tap. Failures are swallowed — the Tap to
    // Pay flow still does its own warm.
    //
    // Skipped entirely when location hasn't been granted yet: warming would
    // raise a permission dialog here (directly on Android, where the plugin asks
    // inside initialize()), and permission is only ever requested from the
    // explicit Tap to Pay action. Once granted, this runs on every open.
    useEffect(() => {
        if (!isOpen || !canUseTapToPay || !tapToPaySupport?.supported || !terminalLocationId) return;

        void (async () => {
            if (!(await hasTapToPayLocationPermission())) return;
            await ensureReaderWarm(terminalLocationId);
            setTapToPayOnboarded(await hasCompletedTapToPayOnboarding());
        })().catch(() => {});
    }, [isOpen, canUseTapToPay, tapToPaySupport?.supported, terminalLocationId]);

    const tapToPaySetup = useTapToPaySetup(terminalLocationId);

    // Setup enrols the device, so the cached flag is stale the moment it finishes.
    // Without this, pressing Tap to Pay straight after setup would kick off setup
    // all over again instead of taking the payment.
    useEffect(() => {
        if (tapToPaySetup.status !== 'done') return;
        void hasCompletedTapToPayOnboarding().then(setTapToPayOnboarded);
    }, [tapToPaySetup.status]);

    // Apple's one-time per-device provisioning runs on the first successful
    // connect and takes the better part of a minute. Offer to get it over with
    // here, before a customer's card is out.
    // Stays mounted through 'done' as well: completing setup flips tapToPayOnboarded
    // true, and without this the block — and with it §4.2's "Try it out" invitation —
    // would unmount the instant setup succeeded.
    const needsTapToPaySetup = canUseTapToPay
        && (!tapToPayOnboarded || tapToPaySetup.status === 'done')
        && (tapToPaySupport?.supported ?? false)
        && isStripeReady
        && Boolean(terminalLocationId);

    // First contact with an email — prefills the digital receipt Apple requires
    // after every transaction, approved or declined (checklist §5.9).
    const receiptEmail = payable.receiptEmail;

    // ── Tap to Pay: PaymentIntent prefetch ────────────────────────────
    // Creating the card_present intent (gateway → DispatchData → DispatchPayments →
    // Stripe) is the last thing on the critical path once the reader is warm, so it
    // runs while the user is still looking at the balance. Apple budgets one second
    // from button press to the Tap to Pay sheet.
    const canTapToPay = canUseTapToPay && (tapToPaySupport?.supported ?? false) && isStripeReady;
    const tapToPayAmountCents = Math.round(tapToPayAmount * 100);
    // Manual card entry has no Apple constraint (unlike the Tap to Pay button, which
    // must never be disabled), so it can gate on Stripe being usable and an amount.
    const canChargeManually = isStripeReady && tapToPayAmount > 0;

    // Never re-sync while a checkout sub-modal is up: a successful payment
    // refetches the job, which drops the remaining balance to 0 — and the
    // success/receipt screens are still reading this amount.
    useEffect(() => {
        if (!isOpen || showTapToPayModal || showStripeModal) return;
        setTapToPayAmount(defaultAmount > 0 ? defaultAmount : 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, defaultAmount, showTapToPayModal, showStripeModal]);

    useEffect(() => {
        if (!isOpen || !canTapToPay || tapToPayAmountCents <= 0) return;
        if (prefetchedIntentRef.current?.amountCents === tapToPayAmountCents
            && prefetchedIntentRef.current.targetId === targetId) {
            return;
        }

        // Anything cached is for a different amount now — drop it so a stale secret
        // can never be picked up by startPayment.
        prefetchedIntentRef.current = null;

        let cancelled = false;
        const timerId = window.setTimeout(async () => {
            try {
                const { client_secret } = await apiService.createTerminalPaymentIntent({ kind: targetKind, id: targetId }, tapToPayAmountCents / 100);
                if (cancelled || !client_secret) return;
                prefetchedIntentRef.current = { targetId, amountCents: tapToPayAmountCents, clientSecret: client_secret };
                recordTapToPayDiagnostic('prefetch', 'Prefetched Tap to Pay payment intent', { amountCents: tapToPayAmountCents });
            } catch (err: any) {
                // Silent: startPayment creates the intent itself if this missed.
                recordTapToPayDiagnostic('prefetch', 'Payment intent prefetch failed', {
                    error: err?.message ?? String(err),
                    amountCents: tapToPayAmountCents,
                }, 'warn');
            }
        }, INTENT_PREFETCH_DEBOUNCE_MS);

        return () => {
            cancelled = true;
            window.clearTimeout(timerId);
        };
    }, [isOpen, canTapToPay, targetKind, targetId, tapToPayAmountCents]);

    /**
     * The Tap to Pay button is never disabled or hidden (App Review checklist §5.3):
     * pressing it always does something, and when it can't take a payment it says why.
     * An unenrolled device goes straight into Apple's Terms & Conditions acceptance,
     * which is exactly the behaviour Apple asks for.
     */
    const handleTapToPayPress = (requestedAmount: number) => {
        setTapToPayBlockedReason(null);

        if (!canUseTapToPay) {
            setTapToPayBlockedReason(`Ask an admin to enable ${tapToPayName()} for your account.`);
            return;
        }
        if (tapToPaySupport && !tapToPaySupport.supported) {
            setTapToPayBlockedReason(tapToPaySupport.message);
            return;
        }
        if (!isStripeReady || !terminalLocationId) {
            setTapToPayBlockedReason('Connect Stripe in settings before accepting card payments.');
            return;
        }
        if (requestedAmount <= 0) {
            setTapToPayBlockedReason('There is no remaining balance to charge.');
            return;
        }
        if (!tapToPayOnboarded) {
            // Not enrolled yet — this is what opens Apple's Terms & Conditions sheet.
            void tapToPaySetup.runSetup();
            return;
        }

        setTapToPayAmount(requestedAmount);
        setShowTapToPayModal(true);
    };

    const enabledPaymentMethods = paymentConfigs.filter(
        (c: CompanyPaymentMethodConfigDTO) => c.is_enabled
    );

    const allowedMethods = enabledPaymentMethods.filter(
        (c: CompanyPaymentMethodConfigDTO) => allowedPaymentTypes([c.payment_method]).length > 0
    );
    const allMethodLabels = Object.entries(PAYMENT_METHOD_LABELS) as [PaymentType, string][];
    const allowedFallbackLabels = allMethodLabels.filter(([key]) => allowedPaymentTypes([key]).length > 0);
    const hasAllowedMethods = allowedMethods.length > 0 || (enabledPaymentMethods.length === 0 && allowedFallbackLabels.length > 0);
    const recordMethods: PaymentType[] = allowedMethods.length > 0
        ? allowedMethods.map((c: CompanyPaymentMethodConfigDTO) => c.payment_method)
        : allowedFallbackLabels.map(([key]) => key);

    // ── Reset form when modal opens and configs are ready ─────────────
    // Configs load asynchronously (query enabled only when isOpen), so we
    // wait for configsLoading to become false before applying defaults.
    useEffect(() => {
        if (isOpen && !configsLoading) {
            setAmount(defaultAmount > 0 ? defaultAmount : 0);
            setMode('card');
            setPaymentType('Cash');
            setProcessedBy('Company');
            setReference('');

            if (allowedMethods.length > 0) {
                const firstConfig = allowedMethods[0];
                setPaymentType(firstConfig.payment_method);
                setProcessedBy(firstConfig.default_processed_by || 'Company');
            } else if (allowedFallbackLabels.length > 0) {
                setPaymentType(allowedFallbackLabels[0][0]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, configsLoading]);

    useEffect(() => {
        if (!isOpen || showStripeModal) return;

        const root = modalScrollRootRef.current;
        if (!root) return;

        let timeoutId: number | undefined;

        const run = () => {
            requestAnimationFrame(() => scrollFocusedFieldIntoVisualViewport(root));
        };

        const onFocusIn = () => {
            run();
            if (timeoutId !== undefined) window.clearTimeout(timeoutId);
            timeoutId = window.setTimeout(() => scrollFocusedFieldIntoVisualViewport(root), 280);
        };

        root.addEventListener('focusin', onFocusIn);
        window.visualViewport?.addEventListener('resize', run);

        return () => {
            root.removeEventListener('focusin', onFocusIn);
            window.visualViewport?.removeEventListener('resize', run);
            if (timeoutId !== undefined) window.clearTimeout(timeoutId);
        };
    }, [isOpen, showStripeModal]);

    // ── Payment status helper ─────────────────────────────────────────
    const calculatePaymentStatus = (newTotalPaid: number): 'NotPaid' | 'PartiallyPaid' | 'PaidFully' => {
        if (newTotalPaid <= 0) return 'NotPaid';
        if (newTotalPaid >= total) return 'PaidFully';
        return 'PartiallyPaid';
    };

    // ── Add payment mutation ──────────────────────────────────────────
    // The payable updates its own caches (job lists / estimate) and notifies its owner.
    const addPaymentMutation = useMutation({
        mutationFn: async (payment: Partial<PaymentDTO>) => {
            const newTotalPaid = totalPaid + (payment.amount || 0);
            await payable.savePayments(
                [{ operation: 'Add', payment: payment as PaymentDTO }],
                calculatePaymentStatus(newTotalPaid),
            );
        },
        onSuccess: () => {
            addNotification(notificationService.success('Payment recorded successfully'));
            onClose();
        },
        onError: (err: any) => {
            addNotification(notificationService.error(err.message || 'Failed to record payment'));
        },
    });

    // ── Handlers ──────────────────────────────────────────────────────
    const handlePaymentTypeChange = (type: PaymentType) => {
        const config = paymentConfigs.find(
            (c: CompanyPaymentMethodConfigDTO) => c.payment_method === type
        );
        setPaymentType(type);
        setProcessedBy(config?.default_processed_by || 'Company');
    };

    const handleRecordPayment = () => {
        if (!amount || amount <= 0) {
            addNotification(notificationService.error('Please enter a valid amount'));
            return;
        }
        if (amount > remainingBalance) {
            addNotification(
                notificationService.error(
                    `Payment amount exceeds remaining balance of $${remainingBalance.toFixed(2)}`
                )
            );
            return;
        }

        const config = paymentConfigs.find(
            (c: CompanyPaymentMethodConfigDTO) => c.payment_method === paymentType
        );
        const feePercentage = config?.fee_percentage || 0;
        const flatFee = config?.fee_flat_amount || 0;
        const feeAmount = amount * (feePercentage / 100) + flatFee;

        addPaymentMutation.mutate({
            type: paymentType,
            amount,
            payment_dispatcher: processedBy,
            reference,
            notes: '',
            fee_amount: feeAmount,
            payment_date: new Date().toISOString(),
        });
    };

    const handleCardSuccess = () => {
        addNotification(notificationService.success('Payment successful!'));
        // The provider webhook records card payments; the payable refreshes once it lands.
        payable.awaitProviderUpdate();
    };

    // ── Don't render if closed ────────────────────────────────────────
    if (!isOpen) return null;

    const isPending = addPaymentMutation.isPending;

    return (
        <>
            <div
                ref={modalScrollRootRef}
                className="fixed inset-0 flex items-start justify-center overflow-y-auto overscroll-y-contain bg-black/50 backdrop-blur-sm p-4 pt-[max(1rem,env(safe-area-inset-top,0px))] pb-[max(1rem,calc(var(--safe-area-inset-bottom)+var(--keyboard-overlap,0px)))] sm:items-center sm:pt-4 sm:pb-[max(1rem,calc(var(--safe-area-inset-bottom)+var(--keyboard-overlap,0px)))]"
                style={layer.style}
            >
                <div className="mt-[clamp(0.5rem,3vh,1.5rem)] w-full max-w-md rounded-xl bg-white shadow-xl relative animate-in fade-in zoom-in-95 duration-200 sm:mt-0">
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-100">
                        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <DollarSign className="h-5 w-5 text-green-600" />
                            Take Payment
                        </h2>
                        <button
                            onClick={layer.onClose}
                            className="p-1 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-full transition-all"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Balance summary */}
                    <div className="px-4 pt-4 pb-2">
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Total</span>
                            <span className="font-medium">${total.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm mt-1">
                            <span className="text-gray-500">Already Paid</span>
                            <span className="font-medium text-green-600">${totalPaid.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm mt-1 pt-1 border-t border-gray-100">
                            <span className="text-gray-700 font-medium">Remaining Balance</span>
                            <span className={`font-semibold ${remainingBalance > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                                ${remainingBalance.toFixed(2)}
                            </span>
                        </div>
                        {payable.depositAmount != null && defaultAmount < remainingBalance && (
                            <div className="flex justify-between text-sm mt-1">
                                <span className="text-gray-700 font-medium">Down payment due now</span>
                                <span className="font-semibold text-orange-600">${defaultAmount.toFixed(2)}</span>
                            </div>
                        )}
                    </div>

                    {/* Content */}
                    <div className="p-4 space-y-4">
                        {configsLoading ? (
                            <div className="flex justify-center py-6">
                                <LoadingSpinner />
                            </div>
                        ) : (
                            <>
                                {/* ── Amount ───────────────────────────────────
                                    Sits above the payment options so the flow reads
                                    the way Apple's Checkout Flow expects: enter an
                                    amount, then pick a payment option, then tap.
                                    Defaults to the remaining balance and feeds both
                                    the card and the record-payment paths. */}
                                <div>
                                    <label className="text-xs font-medium text-gray-600 mb-1 block">
                                        Amount
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm select-none">
                                            $
                                        </span>
                                        <NumericInput
                                            step="0.01"
                                            min="0"
                                            max={remainingBalance}
                                            value={tapToPayAmount}
                                            onChange={(val) => {
                                                setTapToPayAmount(val);
                                                setAmount(val);
                                            }}
                                            className="w-full pl-7 pr-3 py-2.5 text-base border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary-500"
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>

                                {/* ── Card payment / Record payment toggle ───── */}
                                {hasAllowedMethods && (
                                    <div className="grid grid-cols-2 gap-1 p-1 bg-gray-100 rounded-lg">
                                        {([
                                            { value: 'card', label: 'Card payment', icon: CreditCard },
                                            { value: 'record', label: 'Record payment', icon: DollarSign },
                                        ] as const).map(({ value, label, icon: Icon }) => (
                                            <button
                                                key={value}
                                                type="button"
                                                onClick={() => setMode(value)}
                                                className={`flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors ${mode === value
                                                    ? 'bg-white text-primary-700 shadow-sm'
                                                    : 'text-gray-600 hover:text-gray-900'
                                                    }`}
                                            >
                                                <Icon className="h-4 w-4" />
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {mode === 'card' && (
                                <>
                                {/* ── Tap to Pay ────────────────────────────────
                                    First in the payment-option list and above the
                                    fold, per App Review checklist §5.1/§5.2. Never
                                    disabled, greyed or hidden (§5.3) — press always
                                    acts, and handleTapToPayPress explains any block.
                                    Card payment is the tab selected on open. */}
                                <button
                                    type="button"
                                    onClick={() => handleTapToPayPress(tapToPayAmount)}
                                    className="w-full flex items-center justify-center gap-2 px-3 py-3 text-sm font-semibold rounded-lg border-2 border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:border-purple-400 transition-colors"
                                >
                                    <TapToPayIcon className="h-5 w-5" />
                                    {tapToPayName()}
                                </button>

                                {tapToPayBlockedReason && (
                                    <p className="-mt-1 text-xs text-center text-gray-600">
                                        {tapToPayBlockedReason}
                                    </p>
                                )}

                                {/* ── Enter card manually (Stripe Elements) ─────
                                    Opens the card form directly. It used to go via
                                    an intermediate sheet, but once the amount moved up
                                    here that wrapper held nothing but this one button. */}
                                <button
                                    type="button"
                                    onClick={() => setShowStripeModal(true)}
                                    disabled={!canChargeManually}
                                    className={`w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium rounded-lg border-2 transition-colors ${!canChargeManually
                                        ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                        : 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:border-indigo-300'
                                        }`}
                                >
                                    <CreditCard className="h-4 w-4" />
                                    Enter card manually
                                </button>
                                </>
                                )}

                                {/* ── Record payment ───────────────────────────── */}
                                {hasAllowedMethods && mode === 'record' && (
                                <div className="space-y-4">
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-900 mb-2">
                                            Select Payment Method
                                        </h3>
                                        <div
                                            className="grid gap-2"
                                            style={{ gridTemplateColumns: `repeat(${recordMethods.length <= 4 ? recordMethods.length : 3}, minmax(0, 1fr))` }}
                                        >
                                            {recordMethods.map((type) => {
                                                const { icon: Icon, color } = PAYMENT_METHOD_ICONS[type];
                                                const selected = paymentType === type;
                                                return (
                                                    <button
                                                        key={type}
                                                        type="button"
                                                        onClick={() => handlePaymentTypeChange(type)}
                                                        className={`flex flex-col items-center justify-center gap-1.5 px-1 py-3 min-h-[76px] rounded-xl border-2 text-xs font-medium text-center leading-tight transition-colors ${selected
                                                            ? 'border-primary-500 bg-primary-50 text-gray-900'
                                                            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                                                            }`}
                                                    >
                                                        <Icon className={`h-6 w-6 ${color}`} />
                                                        {PAYMENT_METHOD_LABELS[type]}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-xs font-medium text-gray-600 mb-1 block">
                                                Reference
                                            </label>
                                            <input
                                                type="text"
                                                value={reference}
                                                onChange={(e) => setReference(e.target.value)}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary-500"
                                                placeholder="Optional"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-600 mb-1 block">
                                                Processed By
                                            </label>
                                            <CustomSelect
                                                value={processedBy}
                                                onChange={(value) =>
                                                    setProcessedBy(value as PaymentDispatcher)
                                                }
                                                options={[
                                                    { value: 'Company', label: 'Company' },
                                                    { value: 'Technician', label: 'Technician' },
                                                ]}
                                                placeholder="Select processor"
                                            />
                                        </div>
                                    </div>

                                    {/* Record Payment button */}
                                    <button
                                        type="button"
                                        onClick={handleRecordPayment}
                                        disabled={isPending || !amount || amount <= 0}
                                        className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {isPending && <LoadingSpinner />}
                                        <DollarSign className="h-4 w-4" />
                                        Save
                                    </button>
                                </div>
                                )}

                                {mode === 'card' && needsTapToPaySetup && (
                                    <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                        <p className="text-xs text-purple-900 font-medium">
                                            {tapToPaySetup.status === 'done'
                                                ? `${tapToPayName()} is ready on this phone`
                                                : `Set up ${tapToPayName()} on this phone`}
                                        </p>
                                        <p className="mt-1 text-xs text-purple-700">
                                            {tapToPaySetup.status === 'done'
                                                ? 'Take a real payment now to see how it works end to end.'
                                                : tapToPaySetupPrompt()}
                                        </p>
                                        {tapToPaySetup.message && (
                                            <p className={`mt-2 text-xs ${tapToPaySetup.status === 'error' ? 'text-error-600' : 'text-purple-700'}`}>
                                                {tapToPaySetup.message}
                                            </p>
                                        )}
                                        {tapToPaySetup.status !== 'done' ? (
                                            <button
                                                type="button"
                                                onClick={tapToPaySetup.runSetup}
                                                disabled={tapToPaySetup.status === 'running'}
                                                className="mt-2 flex items-center justify-center gap-2 px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                {tapToPaySetup.status === 'running' && <LoadingSpinner />}
                                                {tapToPaySetup.status === 'error' ? 'Try again' : 'Set up now'}
                                            </button>
                                        ) : (
                                            /* §4.2: once Terms are accepted and the tutorial is done,
                                               invite the merchant to actually try it. Lives here rather
                                               than behind the education modal so it also appears on
                                               iOS 18, where Apple's own sheet gives us no completion
                                               callback to chain from. */
                                            <button
                                                type="button"
                                                onClick={() => handleTapToPayPress(tapToPayAmount)}
                                                className="mt-2 flex items-center justify-center gap-2 px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-md hover:bg-purple-700 transition-colors"
                                            >
                                                <TapToPayIcon className="h-3.5 w-3.5" />
                                                Try it out
                                            </button>
                                        )}
                                    </div>
                                )}

                                {mode === 'card' && !isStripeConnected && (
                                    <p className="text-xs text-center text-gray-400">
                                        Connect Stripe in settings to enable card payments
                                    </p>
                                )}
                                {mode === 'card' && isStripeConnected && !isStripeReady && (
                                    <p className="text-xs text-center text-amber-500">
                                        Stripe account onboarding is incomplete — charges are disabled
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            <LayerDepthContext.Provider value={layer.depth}>
            {/* Manual card entry — charges the amount entered above. */}
            <StripePaymentModal
                isOpen={showStripeModal}
                onClose={() => setShowStripeModal(false)}
                target={payable.target}
                connectedAccountId={connectedAccountId}
                totalAmount={tapToPayAmount}
                onSuccess={handleCardSuccess}
                onDismiss={() => {
                    setShowStripeModal(false);
                    onClose();
                }}
            />

            {/* Tap to Pay sub-modal — one instance shared by both entry points. */}
            <TapToPayModal
                isOpen={showTapToPayModal}
                onClose={() => setShowTapToPayModal(false)}
                target={payable.target}
                totalAmount={tapToPayAmount}
                clientEmail={receiptEmail}
                invoiceNumber={payable.documentNumber}
                onSuccess={handleCardSuccess}
                onDismiss={() => {
                    setShowTapToPayModal(false);
                    onClose();
                }}
                terminalLocationId={terminalLocationId}
                tapToPay={tapToPay}
                prefetchedIntentRef={prefetchedIntentRef}
            />
            </LayerDepthContext.Provider>
        </>
    );
}
